/**
 * UPT-2096 (L2, web §5.1) — liste des pièces de l'étape 1 du dialogue d'import :
 * une ligne par pièce (HTML par rang, Markdown, images) avec type, nom, taille,
 * rang et réordonnancement des HTML, rôle des `.md`, « Retirer ». Présentation
 * seule : ne reçoit que des noms, des tailles et des rôles — jamais un
 * document importé à rendre. Lignes empilées sur mobile, noms en `dir="ltr"`.
 */
import { useCallback } from 'react';
import { useIntl } from '@/react-intl';
import { PROTOTYPE_DOCUMENT_ROLES, type PrototypeDocumentRole } from '../data/contracts';
import { prototypeDocumentRoleLabel } from './prototypeI18n';

export interface PieceListHtml {
  fileName: string;
  sizeBytes: number;
}
export interface PieceListMarkdown {
  fileName: string;
  sizeBytes: number;
  role: PrototypeDocumentRole;
}
export interface PieceListImage {
  fileName: string;
  sizeBytes: number;
  thumbnailUrl?: string;
}

export interface ImportPieceListProps {
  htmls: readonly PieceListHtml[];
  markdowns: readonly PieceListMarkdown[];
  images: readonly PieceListImage[];
  onMoveHtml: (from: number, to: number) => void;
  onRemoveHtml: (index: number) => void;
  onRoleChange: (index: number, role: PrototypeDocumentRole) => void;
  onRemoveMarkdown: (index: number) => void;
  onRemoveImage: (index: number) => void;
  /** Annonce `aria-live` (changement de rôle). */
  announcement: string;
}

const KIB = 1024;
const MIB = KIB * KIB;

/** Taille lisible (Ko en dessous de 1 Mo), formatée selon la langue active. */
export const formatPieceSize = (
  intl: { formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string },
  bytes: number
): string =>
  bytes >= MIB
    ? intl.formatNumber(bytes / MIB, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 })
    : intl.formatNumber(Math.max(1, Math.round(bytes / KIB)), { style: 'unit', unit: 'kilobyte' });

export default function ImportPieceList({
  htmls,
  markdowns,
  images,
  onMoveHtml,
  onRemoveHtml,
  onRoleChange,
  onRemoveMarkdown,
  onRemoveImage,
  announcement,
}: ImportPieceListProps) {
  const intl = useIntl();
  const t = useCallback(
    (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values),
    [intl]
  );
  const formatSize = (bytes: number): string => formatPieceSize(intl, bytes);

  // eslint-disable-next-line no-restricted-syntax -- i18n-exempt: aucune pièce, la zone de dépôt de l'étape 1 tient lieu d'état vide
  if (htmls.length + markdowns.length + images.length === 0) return null;

  const rowClass =
    'flex flex-col gap-2 rounded-md border border-portal-border bg-portal-surface p-2 sm:flex-row sm:flex-wrap sm:items-center';
  const buttonClass =
    'rounded-md border border-portal-border bg-portal-surface px-2 py-1 text-xs text-portal-ink disabled:opacity-50 pointer-coarse:min-h-11';
  const kindClass = 'w-10 shrink-0 text-xs font-semibold text-portal-ink-muted';

  return (
    <section className="flex flex-col gap-2" aria-label={t('moduleStudio.prototype.dialog.pieces.title')}>
      <h4 className="text-sm font-medium text-portal-ink">{t('moduleStudio.prototype.dialog.pieces.title')}</h4>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {htmls.map((html, index) => (
          <li key={`html-${index}-${html.fileName}`} className={rowClass}>
            <span className={kindClass}>{t('moduleStudio.prototype.dialog.pieces.kind.html')}</span>
            <span dir="ltr" className="min-w-0 flex-1 break-all text-sm text-portal-ink">{html.fileName}</span>
            <span className="text-xs text-portal-ink-muted">{formatSize(html.sizeBytes)}</span>
            <span className="text-xs text-portal-ink-muted">
              {t('moduleStudio.prototype.dialog.pieces.source', { n: index + 1 })}
            </span>
            <span className="flex flex-wrap gap-1">
              <button
                type="button"
                className={buttonClass}
                disabled={index === 0}
                aria-label={t('moduleStudio.prototype.dialog.pieces.moveUpAria', { name: html.fileName })}
                onClick={() => onMoveHtml(index, index - 1)}
              >
                {t('moduleStudio.prototype.dialog.pieces.moveUp')}
              </button>
              <button
                type="button"
                className={buttonClass}
                disabled={index === htmls.length - 1}
                aria-label={t('moduleStudio.prototype.dialog.pieces.moveDownAria', { name: html.fileName })}
                onClick={() => onMoveHtml(index, index + 1)}
              >
                {t('moduleStudio.prototype.dialog.pieces.moveDown')}
              </button>
              <button
                type="button"
                className={buttonClass}
                aria-label={t('moduleStudio.prototype.dialog.pieces.removeAria', { name: html.fileName })}
                onClick={() => onRemoveHtml(index)}
              >
                {t('moduleStudio.prototype.dialog.pieces.remove')}
              </button>
            </span>
          </li>
        ))}
        {markdowns.map((markdown, index) => (
          <li key={`md-${index}-${markdown.fileName}`} className={rowClass}>
            <span className={kindClass}>{t('moduleStudio.prototype.dialog.pieces.kind.markdown')}</span>
            <span dir="ltr" className="min-w-0 flex-1 break-all text-sm text-portal-ink">{markdown.fileName}</span>
            <span className="text-xs text-portal-ink-muted">{formatSize(markdown.sizeBytes)}</span>
            <select
              value={markdown.role}
              aria-label={t('moduleStudio.prototype.dialog.pieces.roleLabel', { name: markdown.fileName })}
              onChange={(event) => onRoleChange(index, event.target.value as PrototypeDocumentRole)}
              className="rounded-md border border-portal-border bg-portal-surface p-1 text-sm text-portal-ink pointer-coarse:min-h-11"
            >
              {PROTOTYPE_DOCUMENT_ROLES.map((role) => (
                <option key={role} value={role}>{prototypeDocumentRoleLabel(t, role)}</option>
              ))}
            </select>
            <button
              type="button"
              className={buttonClass}
              aria-label={t('moduleStudio.prototype.dialog.pieces.removeAria', { name: markdown.fileName })}
              onClick={() => onRemoveMarkdown(index)}
            >
              {t('moduleStudio.prototype.dialog.pieces.remove')}
            </button>
          </li>
        ))}
        {images.map((image, index) => (
          <li key={`img-${index}-${image.fileName}`} className={rowClass}>
            <span className={kindClass}>{t('moduleStudio.prototype.dialog.pieces.kind.image')}</span>
            {image.thumbnailUrl && (
              <img
                src={image.thumbnailUrl}
                alt=""
                className="h-10 w-10 rounded border border-portal-border object-cover"
              />
            )}
            <span dir="ltr" className="min-w-0 flex-1 break-all text-sm text-portal-ink">{image.fileName}</span>
            <span className="text-xs text-portal-ink-muted">{formatSize(image.sizeBytes)}</span>
            <button
              type="button"
              className={buttonClass}
              aria-label={t('moduleStudio.prototype.dialog.pieces.removeAria', { name: image.fileName })}
              onClick={() => onRemoveImage(index)}
            >
              {t('moduleStudio.prototype.dialog.pieces.remove')}
            </button>
          </li>
        ))}
      </ul>
      {markdowns.length > 0 && (
        <p className="text-xs text-portal-ink-muted">{t('moduleStudio.prototype.dialog.pieces.roleHelp')}</p>
      )}
      <p aria-live="polite" className="text-xs text-portal-ink-muted">{announcement}</p>
    </section>
  );
}
